import { z } from "zod";

/**
 * Supply ordering contracts (design 2026-10-25, ADR 0008): what the clinic app sends to place and follow an order
 * and what the admin console sends to confirm, ship or cancel one. Amounts are cents; no card detail, no Stripe
 * identifier on the practice side, and nothing clinical anywhere (an order carries quantities only).
 *
 * Self-contained on purpose: `index.ts` re-exports this file, so it imports nothing from there.
 */

/** What a practice may order online. The SKU is `<skuPrefix>-<tier>`, resolved on the server from the account's plan. */
export const ORDERABLE_PRODUCTS = {
  tray_test_kit_package: { name: "Tray & Test Kit Package", kind: "testing", skuPrefix: "A123-TTK-020" },
  immunotherapy_mixing_kit: { name: "Immunotherapy Mixing Kit", kind: "therapy", skuPrefix: "A123-IMK" },
} as const;
export const ORDERABLE_PRODUCT_KEYS = ["tray_test_kit_package", "immunotherapy_mixing_kit"] as const;
export const OrderableProduct = z.enum(ORDERABLE_PRODUCT_KEYS);
export type OrderableProduct = z.infer<typeof OrderableProduct>;

export const SUPPLY_ORDER_STATUSES = ["received", "confirmed", "shipped", "delivered", "cancelled"] as const;
export const SupplyOrderStatus = z.enum(SUPPLY_ORDER_STATUSES);
export type SupplyOrderStatus = z.infer<typeof SupplyOrderStatus>;
export const OrderPaymentStatus = z.enum(["unpaid", "processing", "paid", "failed"]);
export type OrderPaymentStatus = z.infer<typeof OrderPaymentStatus>;
const OrderPlan = z.enum(["alc", "bas", "bas_m2m", "cor", "cus"]);

/** One line of an order: the SKU the price was resolved for, the product it names (null for a SKU this client does not know), and the unit price snapshot. */
export const SupplyOrderLine = z.object({
  sku: z.string(),
  product: OrderableProduct.nullable(),
  name: z.string(),
  quantity: z.number().int(),
  unitCents: z.number().int(),
});

/** An order as the practice sees it: status, payment status and tracking, never a Stripe identifier. */
export const SupplyOrder = z.object({
  id: z.string(),
  /** `PP YYWW RRRR C`, the number quoted in every notification. */
  orderNumber: z.string(),
  organizationId: z.string(),
  locationId: z.string(),
  status: SupplyOrderStatus,
  /** The plan the prices were resolved under when the order was placed. */
  pricingTier: OrderPlan,
  source: z.enum(["manual", "auto_reorder"]),
  shippingCents: z.number().int(),
  paymentStatus: OrderPaymentStatus,
  /** Lines plus shipping, from the snapshots; Stripe's invoice total is authoritative once confirmed. */
  totalCents: z.number().int(),
  confirmedAt: z.string().nullable(),
  shippedAt: z.string().nullable(),
  carrier: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lines: z.array(SupplyOrderLine),
});

/** The staff view adds who placed it, the organization's name and the Stripe invoice. */
export const StaffSupplyOrder = SupplyOrder.extend({
  organizationName: z.string().nullable(),
  placedByUserId: z.string().nullable(),
  stripeInvoiceId: z.string().nullable(),
});

/** `POST /billing/orders`: `sku` names the product; the tiered Stripe SKU is chosen on the server from the account's plan. */
export const PlaceOrderRequest = z.object({
  locationId: z.string().uuid(),
  sku: OrderableProduct,
  quantity: z.number().int().min(1).max(10),
});
export const OrderResponse = z.object({ order: SupplyOrder });
export const OrdersResponse = z.object({ orders: z.array(SupplyOrder) });
export const StaffOrderResponse = z.object({ order: StaffSupplyOrder });
export const StaffOrdersResponse = z.object({ orders: z.array(StaffSupplyOrder) });

/** `POST /admin/billing/orders/:orderId/confirm`. Shipping in cents, typed by staff; forced to 0 for a Core-tier order. */
export const ConfirmOrderRequest = z.object({ shippingCents: z.number().int().min(0).max(1_000_000), version: z.number().int().min(1) });
/** `POST /admin/billing/orders/:orderId/ship`. */
export const ShipOrderRequest = z.object({ carrier: z.string().trim().min(1).max(80), trackingNumber: z.string().trim().min(1).max(120), version: z.number().int().min(1) });
/** `POST /admin/billing/orders/:orderId/cancel`; the practice's own cancel takes no body. */
export const CancelOrderRequest = z.object({ version: z.number().int().min(1) });

/** `GET /billing/summary`: the account as the practice may see it (no Stripe identifiers) and how many orders are still open. */
export const BillingSummaryResponse = z.object({
  account: z.object({ plan: OrderPlan, paymentMethodStatus: z.enum(["none", "saved"]), status: z.enum(["active", "suspended", "closed"]) }).nullable(),
  openOrders: z.number().int(),
});

export type SupplyOrderLine = z.infer<typeof SupplyOrderLine>;
export type SupplyOrder = z.infer<typeof SupplyOrder>;
export type StaffSupplyOrder = z.infer<typeof StaffSupplyOrder>;
export type PlaceOrderRequest = z.infer<typeof PlaceOrderRequest>;
export type OrderResponse = z.infer<typeof OrderResponse>;
export type OrdersResponse = z.infer<typeof OrdersResponse>;
export type StaffOrderResponse = z.infer<typeof StaffOrderResponse>;
export type StaffOrdersResponse = z.infer<typeof StaffOrdersResponse>;
export type ConfirmOrderRequest = z.infer<typeof ConfirmOrderRequest>;
export type ShipOrderRequest = z.infer<typeof ShipOrderRequest>;
export type CancelOrderRequest = z.infer<typeof CancelOrderRequest>;
export type BillingSummaryResponse = z.infer<typeof BillingSummaryResponse>;
